/* =========================================================================
 * SiteFooter — the 2026 redesigned site footer.
 *
 * Drop-in replacement for `examples/Footers/DefaultFooter` on the redesigned
 * pages. Left-aligned brand + tagline + socials, right-aligned link columns
 * (one per menu group from `footer.routes.js`). Stripe-style: lots of
 * whitespace, restrained typography, single brand-blue divider.
 *
 * The legacy DefaultFooter is kept intact for pages that haven't been
 * migrated yet so this change is non-breaking.
 * ========================================================================= */

import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import brippleIcon from "assets/images/logos/bripplex/icon.svg";

function SiteFooter({ content }) {
  const { socials, menus, copyright } = content;

  return (
    <MKBox
      component="footer"
      sx={({ palette }) => ({
        backgroundColor: palette.dark.main,
        color: palette.white.main,
        pt: { xs: 8, md: 10 },
        pb: { xs: 5, md: 6 },
      })}
    >
      <Container>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* Left — brand + tagline + socials */}
          <Grid item xs={12} md={4}>
            <MKBox component={Link} to="/" display="flex" alignItems="center" sx={{ gap: 1.5 }}>
              <MKBox
                component="img"
                src={brippleIcon}
                alt="Bripplex"
                sx={{ height: 32, width: "auto" }}
              />
              <MKTypography
                variant="h6"
                sx={({ palette }) => ({
                  color: palette.white.main,
                  letterSpacing: "-0.01em",
                  fontWeight: 700,
                })}
              >
                bripplex
              </MKTypography>
            </MKBox>
            <MKTypography
              variant="body2"
              mt={2}
              sx={({ palette }) => ({
                color: palette.grey[400],
                lineHeight: 1.55,
                maxWidth: 280,
              })}
            >
              A community-powered environmental intelligence network. Researchers gain real-world
              data; contributors gain recognition and impact.
            </MKTypography>
            <MKBox mt={3} display="flex" gap={1.5}>
              {socials.map(({ icon, link }) => (
                <MKTypography
                  key={link}
                  component="a"
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  variant="h6"
                  sx={({ palette }) => ({
                    color: palette.grey[400],
                    transition: "color 0.15s ease",
                    "&:hover": { color: palette.info.main },
                    display: "inline-flex",
                  })}
                >
                  {icon}
                </MKTypography>
              ))}
            </MKBox>
          </Grid>

          {/* Right — link columns */}
          {menus.map(({ name: title, items }) => (
            <Grid key={title} item xs={6} md={2}>
              <MKTypography
                variant="overline"
                sx={({ palette }) => ({
                  color: palette.grey[500],
                  letterSpacing: "0.1em",
                  fontWeight: 700,
                  display: "block",
                  mb: 1.5,
                })}
              >
                {title}
              </MKTypography>
              <MKBox component="ul" p={0} m={0} sx={{ listStyle: "none" }}>
                {items.map(({ name, route, href }) => {
                  const baseSx = ({ palette }) => ({
                    color: palette.grey[300],
                    transition: "color 0.15s ease",
                    "&:hover": { color: palette.white.main },
                    display: "block",
                    py: 0.5,
                  });
                  return (
                    <MKBox key={name} component="li" p={0} m={0}>
                      {href ? (
                        <MKTypography
                          component="a"
                          href={href}
                          target={href.startsWith("http") ? "_blank" : undefined}
                          rel={href.startsWith("http") ? "noreferrer" : undefined}
                          variant="body2"
                          sx={baseSx}
                        >
                          {name}
                        </MKTypography>
                      ) : (
                        <MKTypography component={Link} to={route} variant="body2" sx={baseSx}>
                          {name}
                        </MKTypography>
                      )}
                    </MKBox>
                  );
                })}
              </MKBox>
            </Grid>
          ))}
        </Grid>

        <MKBox
          sx={({ palette }) => ({
            mt: { xs: 6, md: 8 },
            pt: 3,
            borderTop: "1px solid rgba(255, 255, 255, 0.08)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            color: palette.grey[500],
          })}
        >
          {copyright}
        </MKBox>
      </Container>
    </MKBox>
  );
}

SiteFooter.propTypes = {
  content: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])).isRequired,
};

export default SiteFooter;
